import { QueryClient } from '@tanstack/react-query';

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Dev auth token for local development
function getDevAuthHeader(): Record<string, string> {
  if (localStorage.getItem('dev_auth') === 'true') {
    // Format: userId:email:roles (as expected by backend)
    const token = '00000000-0000-0000-0000-000000000001:dev@example.com:contributor,reviewer,publisher,administrator';
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

// Create query client with defaults
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// API error class
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Base fetch function with error handling
export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getDevAuthHeader(),
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Two response shapes in the wild:
    //   1. { error: { code, message, details } }                  (legacy)
    //   2. { error: true, code, message, data }                   (feature 016)
    const code = errorData.error?.code ?? errorData.code ?? 'UNKNOWN_ERROR';
    const message = errorData.error?.message ?? errorData.message ?? 'An error occurred';
    const details = errorData.error?.details ?? errorData.data;
    throw new ApiError(response.status, code, message, details);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json();
  return data.data as T;
}

// Paginated response shape expected by frontend consumers
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Fetch paginated data, transforming the backend { data: [...], meta: {...} } envelope
async function apiFetchPaginated<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<PaginatedResult<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getDevAuthHeader(),
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Two response shapes in the wild:
    //   1. { error: { code, message, details } }                  (legacy)
    //   2. { error: true, code, message, data }                   (feature 016)
    const code = errorData.error?.code ?? errorData.code ?? 'UNKNOWN_ERROR';
    const message = errorData.error?.message ?? errorData.message ?? 'An error occurred';
    const details = errorData.error?.details ?? errorData.data;
    throw new ApiError(response.status, code, message, details);
  }

  const json = await response.json();
  return {
    items: json.data ?? [],
    total: json.meta?.total ?? 0,
    page: json.meta?.page ?? 1,
    limit: json.meta?.limit ?? 20,
    hasMore: json.meta?.hasMore ?? false,
  };
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, options?: RequestInit) =>
    apiFetch<T>(endpoint, { ...options, method: 'GET' }),

  getPaginated: <T>(endpoint: string, options?: RequestInit) =>
    apiFetchPaginated<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestInit) =>
    apiFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestInit) =>
    apiFetch<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default api;
